import { Configuration } from "@azure/msal-browser";
import { RegisterApplicationInsights } from "./AppInsight";

export const environmentUrls = {
    Development: 'https://app-dev-eastus.grouplibrary.bcbssc.com',
    System: 'https://app-sys-eastus.grouplibrary.bcbssc.com',
    Production: 'https://app-prod-eastus.grouplibrary.bcbssc.com',
};

export const crmEnvironments = {
    Development: {
        baseUrl: 'https://bcbsonlinedev.crm.dynamics.com',
        appId: '1d3e2423-40b4-46b0-962f-d436676c9415',
    },
    System: {
        baseUrl: 'https://bcbsonlinesys.crm.dynamics.com',
        appId: 'be2013bc-a215-ed11-b83c-00224820a835',
    },
    Production: {
        baseUrl: 'https://bcbsonline.crm.dynamics.com',
        appId: 'be2013bc-a215-ed11-b83c-00224820a835',
    },
};

export interface ISprintInfoItem {
    releaseTime: number;
    systemVersion: number;
    productionVersion: number;
}

export interface ISprintInfo {
    sprint17: ISprintInfoItem;
    sprint18: ISprintInfoItem;
    sprint21: ISprintInfoItem;
    sprint22: ISprintInfoItem;
    sprint24: ISprintInfoItem;
    sprint30: ISprintInfoItem;
    sprint33: ISprintInfoItem;
    sprint33Patch: ISprintInfoItem;
    sprint34: ISprintInfoItem;
    sprint35: ISprintInfoItem;
    sprint40: ISprintInfoItem;
}


export interface IConstants {
    env: 'Development' | 'System' | 'Production';
    portal: {
        baseUrl: string;
        scopes: string[];
    }
    crm: {
        baseUrl: string;
        scopes: string[];
    }
    appInsights: {
        connectionString: string | null;
    }
    app: {
        authorizationStorageKey: string;
        groupLibraryURL: string;
        crmAppId: string;
        version: string | undefined;
        isLocalHost: boolean;
        cacheKey: string | null;
    },
    msalConfig: Configuration,
    sprintInfo: ISprintInfo,
    data: {
        viewSortOrderUpdateTime: number;
        nonStandardStaticColumnSortTime: number;
    }
    initialize: () => Promise<IConstants>;
}

const constants: IConstants = {
    env: "Development",
    crm: {
        baseUrl: crmEnvironments.Development.baseUrl,
        scopes: ['https://bcbsonlinedev.crm.dynamics.com/user_impersonation'],
    },
    portal: {
        baseUrl: 'https://localhost:44350',
        scopes: ['api://b3d9d9e7-a44b-4a50-84d4-6bf38ab844cd/Users']
    },
    appInsights: {
        connectionString: null
    },
    app: {
        authorizationStorageKey: "authentication",
        groupLibraryURL: environmentUrls.Development,
        crmAppId: '975825e9-352c-ec11-b6e5-000d3a5b93e5',
        version: process.env.REACT_APP_VERSION,
        isLocalHost: window.location.hostname.indexOf('local') >= 0,
        cacheKey: null
    },
    msalConfig: {
        auth: {
            clientId: "fa05cbcb-4226-4fc7-918f-201882dbc0ac",
            redirectUri: "https://localhost:44350/",
            authority: "https://login.microsoftonline.com/d560165e-85d7-436f-a978-c588cf12ebe1",
        },
        cache: {
            // localStorage or sessionStorage - choose localStorage to make sure new tabs are pre-authed
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false,
        },
    },

    // Sprint Information
    // ---------------------------------------------------------------------------------------------------------
    // BCBS Configuration Release
    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entitylist&etn=bcbs_config_release&viewid=decb7a5a-f4a9-ec11-983f-0022480c53bc&viewType=1039
    sprintInfo: {
        sprint17: {
            releaseTime: new Date('2023-07-05 03:29:26.000Z').getTime(),
            systemVersion: 1027,
            productionVersion: 1027,
        },
        sprint18: {
            releaseTime: new Date('2023-07-05 03:29:26.000Z').getTime(),
            systemVersion: 1032,
            productionVersion: 1032,
        },
        sprint21: {
            releaseTime: new Date('2023-10-13 22:32:44.000Z').getTime(),
            systemVersion: 1050,
            productionVersion: 1050,
        },
        sprint22: {
            releaseTime: new Date('2023-11-07 15:36:00.000Z').getTime(),
            systemVersion: 1054,
            productionVersion: 1054,
        },
        sprint24: {
            releaseTime: new Date('2024-01-03 03:56:50.535Z').getTime(),
            systemVersion: 1061,
            productionVersion: 1061,
        },
        sprint30: {
            releaseTime: new Date('2024-06-28T15:14:22.102Z').getTime(),
            systemVersion: 1076,
            productionVersion: 1076,
        },
        sprint33: {
            releaseTime: new Date('2024-09-28T15:14:22.102Z').getTime(),
            systemVersion: 1083,
            productionVersion: 1083,
        },
        sprint33Patch: {
            releaseTime: new Date('2024-10-02T18:51:46.159Z').getTime(),
            systemVersion: 1088,
            productionVersion: 1088,
        },
        sprint34: {
            releaseTime: new Date('2024-11-08T15:14:22.102Z').getTime(),
            systemVersion: 1093,
            productionVersion: 1093,
        },
        sprint35: {
            releaseTime: new Date('2025-04-04T15:14:22.102Z').getTime(),
            systemVersion: 1107,
            productionVersion: 1107,
        },
        sprint40: {
            releaseTime: new Date('2025-07-11T20:17:07.369Z').getTime(),
            systemVersion: 1146,
            productionVersion: 1114, // 1113
        },    
    },


    // Data-related constants, such as view sort order update time and non-standard static column sort time.
    // These constants define important timestamps for data handling, such as when the view sort order was last updated and when non-standard static column sorting was last applied.
    data: {
        viewSortOrderUpdateTime: new Date('2026-10-29T01:00:00.000Z').getTime(),
        nonStandardStaticColumnSortTime:  new Date("2026-10-26T01:00:00.000Z").getTime()
    },

    initialize: async () => {

        const addWebAppSettingsAsync = async () => {

            const updateEnvironment = (environment: string) => {
                if (environment === 'Production') {
                    constants.env = environment;
                    constants.crm.baseUrl = crmEnvironments.Production.baseUrl;
                    constants.crm.scopes = ['https://bcbsonline.crm.dynamics.com/user_impersonation'];
                    constants.app.crmAppId = crmEnvironments.Production.appId;
                    constants.app.groupLibraryURL = environmentUrls.Production;
                    constants.portal.baseUrl = environmentUrls.Production;
                    constants.portal.scopes = ['api://4950e82c-3c15-4b64-8764-48b2a51ab48c/Users'];
                    constants.appInsights.connectionString = 'InstrumentationKey=0e710464-28a9-49d7-871d-04f4248c2d2b;IngestionEndpoint=https://eastus-8.in.applicationinsights.azure.com/;LiveEndpoint=https://eastus.livediagnostics.monitor.azure.com/;ApplicationId=1cafcdfa-1708-43bb-b511-a515f5231591';
                    constants.msalConfig.auth.redirectUri = 'https://app-prod-eastus.grouplibrary.bcbssc.com';
                    constants.msalConfig.auth.clientId = '4d85e837-ef84-4486-8d44-c9c7e99449c8';
                } else if (environment === 'System') {
                    constants.env = environment;
                    constants.crm.baseUrl = crmEnvironments.System.baseUrl;
                    constants.crm.scopes = ['https://bcbsonlinesys.crm.dynamics.com/user_impersonation'];
                    constants.app.crmAppId = crmEnvironments.System.appId;
                    constants.app.groupLibraryURL = environmentUrls.System;
                    constants.portal.baseUrl = environmentUrls.System;
                    constants.portal.scopes = ['api://b3d9d9e7-a44b-4a50-84d4-6bf38ab844cd/Users'];
                    constants.appInsights.connectionString = 'InstrumentationKey=b88d398e-62bd-448f-99c1-b89771268237;IngestionEndpoint=https://eastus-8.in.applicationinsights.azure.com/;LiveEndpoint=https://eastus.livediagnostics.monitor.azure.com/;ApplicationId=27f0247b-def6-4bfb-b81f-b803bf39bf66';
                    constants.msalConfig.auth.redirectUri = 'https://app-sys-eastus.grouplibrary.bcbssc.com/';
                    constants.msalConfig.auth.clientId = 'fa05cbcb-4226-4fc7-918f-201882dbc0ac';
                } else if (environment === 'Development') {
                    constants.env = environment;
                    constants.crm.baseUrl = crmEnvironments.Development.baseUrl;
                    constants.crm.scopes = ['https://bcbsonlinedev.crm.dynamics.com/user_impersonation'];
                    constants.app.crmAppId = crmEnvironments.Development.appId;
                    constants.app.groupLibraryURL = environmentUrls.Development;
                    constants.portal.baseUrl = environmentUrls.Development;
                    constants.portal.scopes = ['api://b3d9d9e7-a44b-4a50-84d4-6bf38ab844cd/Users'];
                    constants.appInsights.connectionString = 'InstrumentationKey=4c574a50-a0ce-4016-b846-12408c290fe8;IngestionEndpoint=https://eastus-8.in.applicationinsights.azure.com/;LiveEndpoint=https://eastus.livediagnostics.monitor.azure.com/;ApplicationId=e2b92b67-9dda-4c6f-b7d8-023171a47af5';
                    constants.msalConfig.auth.redirectUri = 'https://app-dev-eastus.grouplibrary.bcbssc.com/';
                    constants.msalConfig.auth.clientId = 'fa05cbcb-4226-4fc7-918f-201882dbc0ac';
                }


                if (window.location.hostname.indexOf('local') >= 0) {

                    // Faking dev redirection for prod case, currently prod is not authenticating msal with localhost redirect.
                    // we can remove this if that is ever setup properly.
                    constants.portal = {
                        baseUrl: 'https://localhost:44350',
                        scopes: ['api://b3d9d9e7-a44b-4a50-84d4-6bf38ab844cd/Users']
                    };

                    constants.msalConfig = {
                        auth: {
                            clientId: "fa05cbcb-4226-4fc7-918f-201882dbc0ac",
                            redirectUri: "https://localhost:44350/",
                            authority: "https://login.microsoftonline.com/d560165e-85d7-436f-a978-c588cf12ebe1",
                        },
                        cache: {
                            // localStorage or sessionStorage - choose localStorage to make sure new tabs are pre-authed
                            cacheLocation: "localStorage",
                            storeAuthStateInCookie: false,
                        },
                    };

                    // remove app insights connection string for local dev
                    // this is to prevent sending telemetry to the production app insights instance
                    constants.appInsights.connectionString = 'InstrumentationKey=4c574a50-a0ce-4016-b846-12408c290fe8;IngestionEndpoint=https://eastus-8.in.applicationinsights.azure.com/;LiveEndpoint=https://eastus.livediagnostics.monitor.azure.com/;ApplicationId=e2b92b67-9dda-4c6f-b7d8-023171a47af5';

                }

                console.log(`[appl] Constants.updateEnvironment`, environment);
                return constants;

            }

            const configFetch = await fetch('/api/config/EnvironmentName');
            if (configFetch.ok) {
                const configJson = await configFetch.json();
                updateEnvironment(configJson.environmentName);
            } else {
                const configText = await configFetch.text();
                throw new Error(configText);
            }
        }

        const addApplicationInsights = async () => {
            if (constants.appInsights.connectionString) {
                RegisterApplicationInsights(constants.appInsights.connectionString)
            } else {
                console.warn("Application Insights is not ready.");
            }
        }

        const addCacheInfo = async () => {

            // supported by browser?
            if (!window.caches)
                return;

            // cache maintenance
            const cacheKeys = await window.caches.keys();

            // delete old caches that start with app and are not the current cache
            await Promise.all(cacheKeys
                .filter(key => key.startsWith('app.') && !key.startsWith(constants?.app?.cacheKey!))
                .map(key => window.caches.delete(key)));

        }

        const validate = async () => {
            if (constants.env !== 'Development' && constants.env !== 'System' && constants.env !== 'Production') {
                throw new Error(`Invalid environment: ${constants.env}. Expected 'Development', 'System', or 'Production'.`);
            }

            if (constants.env === 'System') {
                // get all sprintInfo items and validate that productionVersion is set
                Object.values(constants.sprintInfo).forEach((item: ISprintInfoItem) => {
                    if (isNaN(item.systemVersion)) {
                        throw new Error(`Production version is not set for sprint: ${item.releaseTime}`);
                    }
                });
            }

            if (constants.env === 'Production') {
                // get all sprintInfo items and validate that productionVersion is set
                Object.values(constants.sprintInfo).forEach((item: ISprintInfoItem) => {
                    if (isNaN(item.productionVersion)) {
                        throw new Error(`Production version is not set for sprint: ${item.releaseTime}`);
                    }
                });
            }
        }

        await addWebAppSettingsAsync();
        await addApplicationInsights();
        await addCacheInfo();
        await validate();
        return constants;
    }

};


export const Constants = constants;
